import { useCallback, useEffect, useRef, useState } from "react";
import {
  isChatOpen,
  subscribeActiveChats,
} from "../../utils/activeChatRegistry";
import { getTokenUserId } from "../../utils/socketAuth";

// Registry source used by the dock's own chat windows.
export const DOCK_SOURCE = "dock";

// The bubble waits for its header (name, picture, order number) so it appears complete. If the
// lookups are slow it is shown anyway after this long, and fills in when they answer.
const READY_FALLBACK_MS = 5000;
const AVATAR_PRELOAD_MS = 3000;
// Presigned avatar URLs expire; header lookups are reused for this long only.
const LOOKUP_TTL_MS = 10 * 60 * 1000;

// Docks outlive the page: every page mounts its own layout (and so its own ChatDock), and the
// provider should not lose an open conversation bubble by navigating. Kept per signed-in user.
const store = { userId: null, docks: [] };
const restoreDocks = () =>
  String(store.userId) === String(getTokenUserId()) ? store.docks : [];

/** Resolve once the image is loaded (or failed / timed out), so it renders with the bubble. */
export const preloadImage = (src, timeoutMs = AVATAR_PRELOAD_MS) =>
  new Promise((resolve) => {
    if (!src) {
      resolve(false);
      return;
    }
    const img = new Image();
    let done = false;
    const finish = (ok) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(ok);
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    img.src = src;
  });

/**
 * Cache an async lookup by key for LOOKUP_TTL_MS (in-flight calls are shared; failures are not
 * cached), so a second message from the same patient opens its bubble instantly.
 */
export const memoizeAsync = (fn) => {
  const cache = new Map();
  return (key) => {
    const k = String(key);
    const hit = cache.get(k);
    if (hit && hit.expires > Date.now()) return hit.promise;
    const promise = Promise.resolve()
      .then(() => fn(key))
      .catch((err) => {
        cache.delete(k);
        throw err;
      });
    cache.set(k, { promise, expires: Date.now() + LOOKUP_TTL_MS });
    return promise;
  };
};

/**
 * State for the minimized chat bubbles (bottom-right).
 *
 * - `open(dock)` adds a bubble once per conversation. Whether it is new is decided from a ref
 *   synchronously; the old code set a flag inside the setState updater, which React may run
 *   later, so the header lookup was sometimes skipped and the bubble stayed "Patient".
 * - A new bubble stays hidden (`ready: false`) until `resolveHeader(dock)` returned and its
 *   picture is loaded, so name, avatar and order number appear together.
 *
 * @param keyOf          (dock) => conversation key
 * @param resolveHeader  async (dock) => ({ title?, subtitle?, avatar? })
 */
export default function useChatDocks({ keyOf, resolveHeader }) {
  const [docks, setDocks] = useState(restoreDocks);
  const docksRef = useRef(docks);
  const idsRef = useRef(new Set(docks.map(keyOf)));
  const resolveRef = useRef(resolveHeader);
  resolveRef.current = resolveHeader;

  useEffect(() => {
    docksRef.current = docks;
    store.userId = getTokenUserId();
    store.docks = docks;
  }, [docks]);

  const update = useCallback(
    (id, patch) => {
      setDocks((prev) =>
        prev.map((x) => (keyOf(x) === id ? { ...x, ...patch } : x)),
      );
    },
    [keyOf],
  );

  const enrich = useCallback(
    async (id, dock) => {
      const fallback = setTimeout(
        () => update(id, { ready: true }),
        READY_FALLBACK_MS,
      );
      const patch = { ready: true };
      try {
        const header = (await resolveRef.current(dock)) || {};
        if (header.avatar) await preloadImage(header.avatar);
        if (header.title) patch.title = header.title;
        if (header.subtitle) patch.subtitle = header.subtitle;
        if (header.avatar) patch.avatar = header.avatar;
      } catch (e) {
        /* keep the placeholder header */
      } finally {
        clearTimeout(fallback);
        update(id, patch);
      }
    },
    [update],
  );

  // Bubbles restored mid-lookup (navigation while it was pending) resume it.
  useEffect(() => {
    docksRef.current
      .filter((d) => !d.ready)
      .forEach((d) => enrich(keyOf(d), d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Add a bubble for this conversation unless one exists. Returns true when added. */
  const open = useCallback(
    (dock) => {
      const id = keyOf(dock);
      if (idsRef.current.has(id)) return false;
      idsRef.current.add(id);
      const entry = { count: 0, expanded: false, ...dock, ready: false };
      setDocks((prev) =>
        prev.some((x) => keyOf(x) === id) ? prev : [...prev, entry],
      );
      enrich(id, entry);
      return true;
    },
    [keyOf, enrich],
  );

  const remove = useCallback(
    (id) => {
      idsRef.current.delete(id);
      setDocks((prev) => prev.filter((x) => keyOf(x) !== id));
    },
    [keyOf],
  );

  const expand = useCallback(
    (id) => {
      // Only one expanded at a time; expanding clears its unread badge.
      setDocks((prev) =>
        prev.map((x) =>
          keyOf(x) === id
            ? { ...x, expanded: true, count: 0 }
            : { ...x, expanded: false },
        ),
      );
    },
    [keyOf],
  );

  const minimize = useCallback(
    (id) => update(id, { expanded: false }),
    [update],
  );

  // A bubble must not show while the same conversation is open in a page-level chat window.
  // When such a chat opens, drop the matching bubble: the provider is reading it there, and new
  // messages create a fresh bubble again once that chat is closed.
  useEffect(() => {
    const prune = () => {
      const drop = docksRef.current.filter((d) =>
        isChatOpen(
          {
            roomId: d.roomId,
            claimRef: d.claimRef,
            appointmentId: d.appointmentId,
            senderId: d.patientUserId,
          },
          { excludeSource: DOCK_SOURCE },
        ),
      );
      if (!drop.length) return;
      const dropIds = new Set(drop.map(keyOf));
      dropIds.forEach((id) => idsRef.current.delete(id));
      setDocks((prev) => prev.filter((d) => !dropIds.has(keyOf(d))));
    };
    prune();
    return subscribeActiveChats(prune);
  }, [keyOf]);

  return { docks, open, update, remove, expand, minimize };
}
